import React, { useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import styled from 'styled-components'

import TextButton from 'components/Luxkit/Button/TextButton'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import useToggle from 'hooks/useToggle'
import ListItem from 'components/Luxkit/List/ListItem'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import Image from 'components/Common/Image'
import { formatCasualDateRange } from 'tripPlanner/utils'
import { isLoggedIn } from 'selectors/accountSelectors'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { accountAccessShowLogin } from 'actions/UiActions'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { omitKeys } from 'lib/objectUtils'
import { useRecentTrips } from 'tripPlanner/hooks/api'
import { BasicTrip } from 'tripPlanner/types/common'
import { PrimaryNotificationDot } from 'tripPlanner/components/NotificationDot'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import config from 'constants/config'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import TextLink from 'components/Luxkit/TextLink'
import LineAngleRightBIcon from 'components/Luxkit/Icons/line/LineAngleRightBIcon'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'

const MenuItem = styled.div`
  position: relative;
`

const ItemOutdent = styled.div`
  margin-left: ${rem(-12)};
  margin-right: ${rem(-12)};
`

const TripImage = styled(Image)`
  border-radius: ${props => props.theme.borderRadius.medium};
`

const BottomButton = styled(TextButton)`
  margin-top: ${rem(20)};
`

const BottomSecondaryButton = styled(TextButton)`
  margin-top: ${rem(10)};
`

const TextWithNotification = styled.div`
  position: relative;
`

const MyTripNotificationDot = styled(PrimaryNotificationDot)`
  position: absolute;
  top: 0;
  left: 100%;
`

const AddIconWrapper = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.five};
  display: flex;
  width: ${rem(48)};
  height: ${rem(48)};
  align-items: center;
  justify-content: center;
`

const TextWrapper = styled.div`
  margin-bottom: ${rem(10)};
`

function getTripSubtitle(trip: BasicTrip): string | undefined {
  if (trip.startDate && trip.endDate) {
    return formatCasualDateRange(trip.startDate, trip.endDate)
  }
}

interface Props {
  isLoggedIn: boolean;
  variant: 'mobile' | 'desktop';
  showTripNotificationDot: boolean;
}

function NavigationHeaderTripsMenu({ isLoggedIn, variant, showTripNotificationDot }: Props) {
  const { data: trips = [], isError, isFetching } = useRecentTrips()
  const isLoading = isFetching && trips.length === 0
  const { value: menuOpen, toggle: toggleMenu, off: closeMenu } = useToggle()
  const triggerRef = useRef()

  const enableMenuV2 = config.TRIP_PLANNER_DISPLAY_MY_BOOKINGS

  const onMenuToggle = useCallback(() => {
    toggleMenu()
    const action = menuOpen ? 'closed' : 'opened'
    Analytics.track('client-event', clientEvent('my_trips_dropdown_menu', action, 'trip_planner', 'interaction'))
  }, [menuOpen, toggleMenu])

  const onMenuClose = useCallback(() => {
    closeMenu()
    Analytics.track('client-event', clientEvent('my_trips_dropdown_menu', 'closed', 'trip_planner', 'interaction'))
  }, [closeMenu])

  const onViewAllTrips = useCallback(() => {
    onMenuClose()
    Analytics.track('client-event', clientEvent('my_trips_dropdown_menu_view_all_option', 'clicked', 'trip_planner', 'interaction'))
  }, [onMenuClose])

  const onViewTrip = useCallback(() => {
    onMenuClose()
    Analytics.track('client-event', clientEvent('my_trips_dropdown_menu_view_trip_option', 'clicked', 'trip_planner', 'interaction'))
  }, [onMenuClose])

  const dispatch = useAppDispatch()

  const title = trips.length ? `My Trips (${trips.length})` : 'My Trips'
  const subtitle = trips.length ? 'Recent activity' : undefined

  const loggedInContentV2 = <>
    <TextWrapper>
      <BodyText variant="medium">
        Plan your next adventure with{' '}
        <TextLink
          href="/trip-planner/about"
          underline
          variant="default"
          weight="regular"
        >
          TripPlanner
        </TextLink>
      </BodyText>
    </TextWrapper>

    <ItemOutdent>
      <ListItem
        size="large"
        key="addNew"
        to="/trip-planner#create"
        title="Create new trip"
        startIcon={
          <AddIconWrapper>
            <LinePlusIcon size="M" />
          </AddIconWrapper>
        }
      />
    </ItemOutdent>
  </>

  const loggedInContentV1 = <>
    <BodyText variant="medium" weight="bold">You don't have any upcoming trips</BodyText>
    <BodyText variant="medium">Let's start planning your next adventure</BodyText>
    <TextButton
      onClick={onMenuClose}
      kind="primary"
      variant="ghost"
      to="/trip-planner/about"
      outdent="left">Learn more
    </TextButton>
  </>

  const loggedOutContentV2 = <BodyText variant="medium">
    Sign in to plan your next adventure with{' '}
    <TextLink
      href="/trip-planner/about"
      underline
      variant="default"
      weight="regular"
    >
      TripPlanner
    </TextLink>{' '}
    and to view your bookings.
  </BodyText>

  const loggedOutContentV1 = <>
    <BodyText variant="medium">Sign in to start planning your next trip!</BodyText>
    <TextButton
      onClick={closeMenu}
      kind="primary"
      variant="ghost"
      to="/trip-planner/about"
      outdent="left"
    >
      Learn more
    </TextButton>
  </>

  if (isLoggedIn && isError) {
    // If trips failed to fetch, just link to the TP landing page
    return <TextButton
      kind="tertiary"
      variant="dark"
      to="/trip-planner"
    >
      My Trips
    </TextButton>
  }

  let content: React.ReactNode
  let buttonProps: React.ComponentProps<typeof TextButton>
  let secondaryButtonProps: React.ComponentProps<typeof TextButton>
  let modalFooterProps: React.ComponentProps<typeof ModalFooter>

  if (trips.length > 0) {
    content = <ItemOutdent>
      {trips.slice(0, 3).map(trip => (
        <ListItem
          size="large"
          key={trip.id}
          to={`/trip-planner/trip/${trip.id}/summary`}
          title={trip.name}
          subtitle={getTripSubtitle(trip)}
          startIcon={<TripImage id={trip.imageId} aspectRatio="1:1" width={48} height={48}/>}
          endIcon={!enableMenuV2 && <LineAngleRightBIcon colour="neutral-two" size="XS"/>}
          onClick={onViewTrip}
        />
      ))}
      {enableMenuV2 && <ListItem
        size="large"
        key="addNew"
        to="/trip-planner#create"
        title="Create new trip"
        startIcon={<AddIconWrapper><LinePlusIcon size="M"/></AddIconWrapper>}
      />}
    </ItemOutdent>
    if (enableMenuV2) {
      buttonProps = {
        kind: 'primary',
        variant: 'default',
        fit: 'full-width',
        onClick: onViewAllTrips,
        to: '/trip-planner',
        children: trips.length === 1 ? 'View trip' : 'View all trips',
      }

      secondaryButtonProps = {
        kind: 'secondary',
        variant: 'default',
        fit: 'full-width',
        onClick: onMenuClose,
        to: '/account/my-escapes',
        children: 'View bookings',
      }
      modalFooterProps = {
        secondaryActionProps: omitKeys(secondaryButtonProps, ['kind']),
        primaryActionProps: omitKeys(buttonProps, ['kind']),
      }
    } else {
      buttonProps = {
        kind: 'secondary',
        variant: 'default',
        fit: 'full-width',
        onClick: onViewAllTrips,
        to: '/trip-planner',
        children:
          trips.length === 1 ? 'View trip' : `View all ${trips.length} trips`,
      }
      modalFooterProps = { secondaryActionProps: omitKeys(buttonProps, ['kind']) }
    }
  } else if (isLoggedIn && isLoading) {
    content = <LoadingIndicator inline/>
  } else if (isLoggedIn) {
    content = <>
      {enableMenuV2 && loggedInContentV2}
      {!enableMenuV2 && loggedInContentV1}
    </>

    if (enableMenuV2) {
      secondaryButtonProps = {
        kind: 'secondary',
        variant: 'default',
        fit: 'full-width',
        onClick: closeMenu,
        to: '/account/my-escapes',
        children: 'View bookings',
      }
      modalFooterProps = {
        secondaryActionProps: omitKeys(secondaryButtonProps, ['kind']),
      }
    } else {
      buttonProps = {
        kind: 'primary',
        fit: 'full-width',
        onClick: onMenuClose,
        to: '/trip-planner#create',
        startIcon: <LinePlusIcon/>,
        children: 'Create new trip',
      }

      modalFooterProps = { primaryActionProps: omitKeys(buttonProps, ['kind']) }
    }
  } else {
    content = <>
      {enableMenuV2 && loggedOutContentV2}
      {!enableMenuV2 && loggedOutContentV1}
    </>

    buttonProps = {
      kind: 'primary',
      fit: 'full-width',
      onClick: () => {
        closeMenu()
        dispatch(accountAccessShowLogin())
      },
      name: 'signup',
      children: 'Sign in',
    }
    modalFooterProps = { primaryActionProps: omitKeys(buttonProps, ['kind']) }
  }

  return <MenuItem>
    <TextButton
      kind="tertiary"
      variant="dark"
      ref={triggerRef}
      startIcon={<LineHeartIcon size="XS" />}
      endIcon={variant === 'desktop' ? <DropdownChevron open={menuOpen} /> : undefined}
      onClick={onMenuToggle}
    >
      <TextWithNotification>TripPlanner{showTripNotificationDot && <MyTripNotificationDot />}</TextWithNotification>
    </TextButton>

    {variant === 'mobile' &&
      <ModalBase isOpen={menuOpen} onClose={onMenuClose}>
        <ModalHeader title={title} subtitle={subtitle} onCloseButtonClick={onMenuClose} />
        <ModalBody>
          <ModalContent>{content}</ModalContent>
        </ModalBody>
        <ModalFooter {...modalFooterProps}/>
      </ModalBase>
    }

    {variant === 'desktop' &&
      <Dropdown
        open={menuOpen}
        onClose={onMenuClose}
        caretType="none"
        triggerRef={triggerRef}
        alignment="right"
      >
        <DropdownContainer
          title={title}
          subtitle={subtitle}
          onClose={onMenuClose}
        >
          {content}
          {buttonProps && <BottomButton {...buttonProps}/>}
          {enableMenuV2 && secondaryButtonProps && (
            <BottomSecondaryButton {...secondaryButtonProps} />
          )}
        </DropdownContainer>

      </Dropdown>
    }
  </MenuItem>
}

function mapStateToProps(state: App.State) {
  return {
    isLoggedIn: isLoggedIn(state),
    showTripNotificationDot: state.tripPlanner.tripItemHasJustBeenAdded,
  }
}

export default connect(mapStateToProps)(NavigationHeaderTripsMenu)
